import { useEffect, useCallback } from 'react';

interface UseKeyboardShortcutProps {
    key: string;
    ctrlKey?: boolean;
    metaKey?: boolean;
    altKey?: boolean;
    shiftKey?: boolean;
    callback: () => void;
    enabled?: boolean;
}

export const useKeyboardShortcut = ({
    key,
    ctrlKey = false,
    metaKey = false,
    altKey = false,
    shiftKey = false,
    callback,
    enabled = true
}: UseKeyboardShortcutProps) => {
    const handleKeyDown = useCallback((event: KeyboardEvent) => {
        if (!enabled) return;

        const isCorrectKey = event.key.toLowerCase() === key.toLowerCase();
        const isCorrectCtrl = ctrlKey ? event.ctrlKey : !event.ctrlKey;
        const isCorrectMeta = metaKey ? event.metaKey : !event.metaKey;
        const isCorrectAlt = altKey ? event.altKey : !event.altKey;
        const isCorrectShift = shiftKey ? event.shiftKey : !event.shiftKey;

        if (isCorrectKey && isCorrectCtrl && isCorrectMeta && isCorrectAlt && isCorrectShift) {
            event.preventDefault();
            event.stopPropagation();
            callback();
        }
    }, [key, ctrlKey, metaKey, altKey, shiftKey, callback, enabled]);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown]);
};

// Specific hook for search toggle (Cmd+S on Mac, Ctrl+S on Windows)
export const useSearchToggle = (callback: () => void, enabled: boolean = true) => {
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;

    useKeyboardShortcut({
        key: 'f',
        ctrlKey: !isMac,
        metaKey: isMac,
        callback,
        enabled
    });
};
